import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import {
  Public as PublicIcon,
  LockOutlined as LockIcon,
  PeopleOutline as PeopleIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { JoinRequestButton } from './JoinRequestButton';
import { InitialsAvatar } from '../ui/InitialsAvatar';
import { StatusPill } from '../ui/StatusPill';
import { cardSx } from '../ui/uiHelpers';

export interface GroupListItem {
  _id: string;
  name: string;
  description: string;
  private: boolean;
  createdAt: string;
  memberCount?: number;
  stats?: {
    activeContestCount: number;
    upcomingContestCount: number;
    totalContestCount: number;
  };
}

// Running challenges matter most, then upcoming ones; quiet groups get plain muted text.
const ChallengeStatus = ({ stats }: { stats: GroupListItem['stats'] }) => {
  const active = stats?.activeContestCount ?? 0;
  const upcoming = stats?.upcomingContestCount ?? 0;
  const { t } = useTranslation('groups');
  if (active > 0) return <StatusPill tone="success" dot label={t('card.challengesLive', { count: active })} />;
  if (upcoming > 0) return <StatusPill tone="accent" label={t('card.upcoming', { count: upcoming })} />;
  return <Box component="span" sx={{ fontSize: 12, color: 'var(--muted)' }}>{t('card.noLive')}</Box>;
};

// The whole card opens the group; groups the viewer hasn't joined get a join action underneath.
export const GroupListCard = ({ group, joined }: { group: GroupListItem; joined: boolean }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('groups');
  const members = group.memberCount ?? 0;

  return (
    <Box
      role="link"
      tabIndex={0}
      onClick={() => navigate(`/groups/${group._id}`)}
      onKeyDown={e => e.key === 'Enter' && navigate(`/groups/${group._id}`)}
      sx={[
        cardSx,
        {
          height: '100%',
          p: '18px',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          cursor: 'pointer',
          transition: 'border-color .15s, box-shadow .15s',
          '&:hover': { borderColor: 'var(--muted)', boxShadow: '0 4px 14px rgba(0,0,0,.06)' },
          '&:hover .group-card-chevron': { color: 'var(--fg)', transform: 'translateX(2px)' },
          '&:focus-visible': { outline: '2px solid var(--primary)', outlineOffset: 2 },
        },
      ]}
    >
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
        <InitialsAvatar name={group.name} size={42} shape="rounded" />
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' }}>
          <Box sx={{ fontSize: 15, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{group.name}</Box>
          <Box sx={{ fontSize: 12, color: 'var(--muted)', display: 'flex', gap: 0.5, alignItems: 'center', '& svg': { fontSize: 14 } }}>
            {group.private ? <LockIcon /> : <PublicIcon />}
            {group.private ? t('visibility.private') : t('visibility.public')}
          </Box>
        </Box>
        <ChevronRightIcon className="group-card-chevron" sx={{ color: 'var(--muted)', transition: 'transform .15s, color .15s' }} />
      </Box>

      {/* Two lines reserved even when short, so cards in a row line up. */}
      <Box
        sx={{
          fontSize: 13, lineHeight: 1.5, minHeight: '3em', color: 'var(--muted)',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
        }}
      >
        {group.description || t('card.noDescription')}
      </Box>

      <Box
        sx={{
          mt: 'auto', pt: 1.5, borderTop: '1px solid var(--border)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap'
        }}
      >
        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', fontSize: 13, '& svg': { fontSize: 16, color: 'var(--muted)' } }}>
          <PeopleIcon />
          {t('card.members', { count: members })}
        </Box>
        <ChallengeStatus stats={group.stats} />
      </Box>

      {!joined && (
        <Box onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
          <JoinRequestButton variant="card" groupId={group._id} userRole={null} isPrivate={group.private} />
        </Box>
      )}
    </Box>
  );
};
